import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlignLeft, AlignCenter, AlignRight, Bold, Italic, CaseUpper, Copy, Trash2, ArrowUpToLine, ArrowDownToLine, ChevronUp, ChevronDown, Upload, Loader2, AlignStartVertical, AlignCenterVertical, AlignEndVertical, AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal } from "lucide-react";
import { FONTS } from "@/lib/card";
import { uploadImage, errMsg } from "@/lib/api";
import { Group, Row, Num, Color, Select, Seg } from "./fields";
import { Slider } from "@/components/ui/slider";

const Btn = ({ children, onClick, testId, title, danger }) => (
  <button onClick={onClick} title={title} data-testid={testId}
    className={`h-8 flex-1 grid place-items-center rounded-md border border-slate-800 bg-slate-950 transition-colors ${danger ? "hover:border-red-500/60 hover:text-red-400" : "hover:border-slate-600 hover:text-white"} text-slate-300`}>{children}</button>
);

function Layout({ el, tpl, onChange }) {
  const al = (k) => ({
    left: { x: 0 }, hcenter: { x: +((tpl.width - el.w) / 2).toFixed(2) }, right: { x: +(tpl.width - el.w).toFixed(2) },
    top: { y: 0 }, vcenter: { y: +((tpl.height - el.h) / 2).toFixed(2) }, bottom: { y: +(tpl.height - el.h).toFixed(2) },
  }[k]);
  return (
    <Group title="Position & size (mm)">
      <div className="grid grid-cols-2 gap-2">
        <Row label="X"><Num value={el.x} onChange={(x) => onChange({ x })} testId="property-x-input" /></Row>
        <Row label="Y"><Num value={el.y} onChange={(y) => onChange({ y })} testId="property-y-input" /></Row>
        <Row label="W"><Num value={el.w} min={1} onChange={(w) => onChange(el.type === "qr" ? { w, h: w } : { w })} testId="property-width-input" /></Row>
        <Row label="H"><Num value={el.h} min={0.1} onChange={(h) => onChange(el.type === "qr" ? { w: h, h } : { h })} testId="property-height-input" /></Row>
      </div>
      <Row label="Rotation"><Num value={el.rotation || 0} step={1} suffix="°" onChange={(rotation) => onChange({ rotation })} testId="property-rotation-input" /></Row>
      <Row label="Opacity">
        <Slider value={[Math.round((el.opacity ?? 1) * 100)]} max={100} step={1} onValueChange={([v]) => onChange({ opacity: v / 100 })} data-testid="property-opacity-slider" />
      </Row>
      <div className="flex gap-1">
        {[["left", AlignStartVertical], ["hcenter", AlignCenterVertical], ["right", AlignEndVertical], ["top", AlignStartHorizontal], ["vcenter", AlignCenterHorizontal], ["bottom", AlignEndHorizontal]].map(([k, I]) => (
          <Btn key={k} onClick={() => onChange(al(k))} title={`Align ${k}`} testId={`align-${k}`}><I size={14} /></Btn>
        ))}
      </div>
    </Group>
  );
}

function TextProps({ el, onChange }) {
  return (
    <Group title="Text">
      <textarea value={el.text} onChange={(e) => onChange({ text: e.target.value })} rows={3}
        className="cf-input text-xs py-2 font-mono resize-y" data-testid="property-text-input" />
      <p className="text-[10px] text-slate-500">Use <span className="font-mono text-indigo-300">{"{{FIELD}}"}</span> placeholders to merge record data.</p>
      <Row label="Font"><Select value={el.fontFamily} onChange={(fontFamily) => onChange({ fontFamily })} options={FONTS} testId="property-font-select" /></Row>
      <div className="grid grid-cols-2 gap-2">
        <Row label="Size"><Num value={el.fontSize} step={0.5} min={1} suffix="pt" onChange={(fontSize) => onChange({ fontSize })} testId="property-font-size-input" /></Row>
        <Row label="Weight"><Select value={String(el.fontWeight)} onChange={(v) => onChange({ fontWeight: +v })} options={["300", "400", "500", "600", "700", "800"]} testId="property-font-weight-select" /></Row>
      </div>
      <Row label="Color"><Color value={el.color} onChange={(color) => onChange({ color })} testId="property-color-picker" /></Row>
      <div className="flex gap-2">
        <Seg value={el.align} onChange={(align) => onChange({ align })} testId="property-align"
          options={[{ value: "left", label: <AlignLeft size={14} /> }, { value: "center", label: <AlignCenter size={14} /> }, { value: "right", label: <AlignRight size={14} /> }]} />
        <div className="flex gap-1 w-28">
          <Btn onClick={() => onChange({ fontWeight: el.fontWeight >= 700 ? 400 : 700 })} testId="property-bold-toggle" title="Bold"><Bold size={14} /></Btn>
          <Btn onClick={() => onChange({ italic: !el.italic })} testId="property-italic-toggle" title="Italic"><Italic size={14} /></Btn>
          <Btn onClick={() => onChange({ uppercase: !el.uppercase })} testId="property-uppercase-toggle" title="Uppercase"><CaseUpper size={14} /></Btn>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Row label="Spacing"><Num value={el.letterSpacing || 0} step={0.1} onChange={(letterSpacing) => onChange({ letterSpacing })} testId="property-letter-spacing-input" /></Row>
        <Row label="Line ht."><Num value={el.lineHeight || 1.15} step={0.05} onChange={(lineHeight) => onChange({ lineHeight })} testId="property-line-height-input" /></Row>
      </div>
    </Group>
  );
}

function ShapeProps({ el, onChange }) {
  return (
    <Group title="Shape">
      <Row label="Fill"><Color value={el.fill} onChange={(fill) => onChange({ fill })} testId="property-fill-color" allowNone /></Row>
      <p className="text-[10px] text-slate-500 -mt-1">Accepts hex or CSS gradients, e.g. linear-gradient(90deg,#B8860B,#FDE68A)</p>
      <Row label="Stroke"><Color value={el.stroke} onChange={(stroke) => onChange({ stroke })} testId="property-stroke-color" allowNone /></Row>
      <div className="grid grid-cols-2 gap-2">
        <Row label="Stroke w"><Num value={el.strokeWidth || 0} step={0.1} min={0} onChange={(strokeWidth) => onChange({ strokeWidth })} testId="property-stroke-width-input" /></Row>
        {el.type === "rect" && <Row label="Radius"><Num value={el.radius || 0} step={0.5} min={0} onChange={(radius) => onChange({ radius })} testId="property-radius-input" /></Row>}
      </div>
    </Group>
  );
}

function ImageProps({ el, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { onChange({ src: await uploadImage(f) }); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Group title="Image">
      <button onClick={() => ref.current?.click()} className="cf-btn-ghost h-8 text-xs w-full" data-testid="property-image-upload-button">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {el.src ? "Replace image" : "Upload image"}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="property-image-file-input" />
      {el.src && <button onClick={() => onChange({ src: "" })} className="text-[11px] text-slate-400 hover:text-red-400" data-testid="property-image-remove">Remove static image</button>}
      <Row label="Data field">
        <input value={el.field || ""} placeholder="e.g. PHOTO" onChange={(e) => onChange({ field: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") })}
          className="cf-input h-8 text-xs font-mono" data-testid="property-variable-bind" />
      </Row>
      <p className="text-[10px] text-slate-500 -mt-1">When set, each record's image for this field replaces the static image.</p>
      <Row label="Fit"><Seg value={el.fit || "cover"} onChange={(fit) => onChange({ fit })} testId="property-image-fit" options={[{ value: "cover", label: "Cover" }, { value: "contain", label: "Contain" }, { value: "fill", label: "Stretch" }]} /></Row>
      <div className="grid grid-cols-2 gap-2">
        <Row label="Radius"><Num value={el.radius || 0} step={0.5} min={0} onChange={(radius) => onChange({ radius })} testId="property-image-radius-input" /></Row>
        <Row label="Border"><Num value={el.strokeWidth || 0} step={0.1} min={0} onChange={(strokeWidth) => onChange({ strokeWidth })} testId="property-image-border-input" /></Row>
      </div>
      <Row label="Border col"><Color value={el.stroke} onChange={(stroke) => onChange({ stroke })} testId="property-image-border-color" /></Row>
    </Group>
  );
}

function QrProps({ el, onChange }) {
  return (
    <Group title="QR code">
      <textarea value={el.value} onChange={(e) => onChange({ value: e.target.value })} rows={2}
        className="cf-input text-xs py-2 font-mono" data-testid="property-qr-value-input" />
      <p className="text-[10px] text-slate-500 -mt-1">Static text/URL or a placeholder like <span className="font-mono text-indigo-300">{"{{CERT_NO}}"}</span>. Use <span className="font-mono text-amber-300">{"{{_VERIFY_URL}}"}</span> for the auto public certificate page.</p>
      <Row label="Color"><Color value={el.color} onChange={(color) => onChange({ color })} testId="property-qr-color" /></Row>
      <Row label="Background"><Color value={el.bg} onChange={(bg) => onChange({ bg })} testId="property-qr-bg" /></Row>
    </Group>
  );
}

function BarcodeProps({ el, onChange }) {
  return (
    <Group title="Barcode">
      <input value={el.value} onChange={(e) => onChange({ value: e.target.value })} className="cf-input h-8 text-xs font-mono" data-testid="property-barcode-value-input" />
      <p className="text-[10px] text-slate-500 -mt-1">Static value or placeholder, e.g. <span className="font-mono text-indigo-300">{"{{EMP_ID}}"}</span> / <span className="font-mono text-indigo-300">{"{{MEMBER_NO}}"}</span></p>
      <Row label="Format"><Select value={el.format || "CODE128"} onChange={(format) => onChange({ format })} options={["CODE128", "CODE39", "EAN13", "UPC", "ITF14"]} testId="property-barcode-format" /></Row>
      <Row label="Color"><Color value={el.color} onChange={(color) => onChange({ color })} testId="property-barcode-color" /></Row>
      <Row label="Background"><Color value={el.bg} onChange={(bg) => onChange({ bg })} testId="property-barcode-bg" allowNone /></Row>
      <div className="grid grid-cols-2 gap-2 items-center">
        <label className="flex items-center gap-2 text-[11px] text-slate-400">
          <input type="checkbox" checked={!!el.showText} onChange={(e) => onChange({ showText: e.target.checked })} data-testid="property-barcode-show-text" /> Show text
        </label>
        <Row label="Size"><Num value={el.fontSize || 5} step={0.5} min={2} suffix="pt" onChange={(fontSize) => onChange({ fontSize })} testId="property-barcode-font-size" /></Row>
      </div>
    </Group>
  );
}

const PROPS = { text: TextProps, rect: ShapeProps, ellipse: ShapeProps, image: ImageProps, qr: QrProps, barcode: BarcodeProps };

export default function Inspector({ el, tpl, onChange, onDelete, onDuplicate, onLayer }) {
  const P = PROPS[el.type];
  return (
    <div className="p-4 space-y-5" data-testid="element-inspector">
      <div className="flex items-center justify-between">
        <p className="font-heading font-bold text-sm capitalize" data-testid="inspector-type">{el.type === "ellipse" ? "Circle" : el.type}</p>
        <span className="text-[10px] font-mono text-slate-500">#{el.id.slice(0, 6)}</span>
      </div>
      <div className="flex gap-1">
        <Btn onClick={() => onLayer("top")} title="Bring to front" testId="layer-bring-front"><ArrowUpToLine size={14} /></Btn>
        <Btn onClick={() => onLayer("up")} title="Forward" testId="layer-forward"><ChevronUp size={14} /></Btn>
        <Btn onClick={() => onLayer("down")} title="Backward" testId="layer-backward"><ChevronDown size={14} /></Btn>
        <Btn onClick={() => onLayer("bottom")} title="Send to back" testId="layer-send-back"><ArrowDownToLine size={14} /></Btn>
        <Btn onClick={onDuplicate} title="Duplicate" testId="element-duplicate-button"><Copy size={14} /></Btn>
        <Btn onClick={onDelete} title="Delete" testId="element-delete-button" danger><Trash2 size={14} /></Btn>
      </div>
      {P && <P el={el} onChange={onChange} />}
      <Layout el={el} tpl={tpl} onChange={onChange} />
    </div>
  );
}
